import React, { useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  pushToHistoryAction,
  setDescription,
  setSubTitle,
  setWheelName,
  undo,
  redo,
} from "../../../../redux/reducers/segmentBuilder";
import {
  getDescription,
  getSubTitle,
  getWheelName,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { handleAiAssistantIcon } from "../../../FroalaEditor/children/FroalaHelpers";
import FroalaTextEditor from "../../../FroalaEditor/children/FroalaTextEditor";
import NewAIPrompt from "../../../NewAiPrompt";
import { getFroalaFocus } from "../../../../redux/selectors/segmentBuilderSelectors";
import { FROALA_FOCUS_ENUM } from "../../../../constants/FroalaFocus.enum";

export default function DetailsTab() {
  const editorRef = useRef(null);
  const editorRef2 = useRef(null);
  const editorRef3 = useRef(null);
  const wheel_name = useSelector(getWheelName);
  const sub_title = useSelector(getSubTitle);
  const description = useSelector(getDescription);
  const froalaFocus = useSelector(getFroalaFocus);
  const dispatch = useDispatch();

  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [currentCompletionType, setCurrentCompletionType] = useState("general");
  const [aiPromptPosition, setAiPromptPosition] = useState({
    top: "25vh",
    right: "35vw",
  });

  // Ref to store timeout ID for debounced state updates
  const debounceTimeoutRef = useRef(null);

  const handleFroalaCustomization = (FroalaEditor) => {
    handleAiAssistantIcon(
      FroalaEditor,
      setShowAiPrompt,
      (editorId, editorElement) => {
        // Set completion type based on which editor the AI assistant was clicked from
        let completionType = "general";
        switch (editorId) {
          case 1:
            completionType = "title";
            break;
          case 2:
            completionType = "subtitle";
            break;
          case 3:
            completionType = "description";
            break;
          default:
            completionType = "general";
        }
        setCurrentCompletionType(completionType);

        // Calculate position relative to the clicked editor
        if (editorElement) {
          const rect = editorElement.getBoundingClientRect();
          // Position the AI prompt to the right of the editor, aligned with the top
          // Add some offset for better visual appearance
          const top = Math.max(20, rect.top + window.scrollY - 100); // 50px offset from top
          const right = Math.max(20, window.innerWidth - rect.right + 350); // 350px offset from right

          setAiPromptPosition({
            top: `${top}px`,
            right: `${right}px`,
          });
        }
      }
    );
  };

  // Helper debounce function to avoid unnecessary state updates
  function debounce(func, wait) {
    return function (...args) {
      // Clear previous timeout if exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Set a new timeout for the debounced action
      debounceTimeoutRef.current = setTimeout(
        () => func.apply(this, args),
        wait
      );
    };
  }

  const handleTitleChange = useCallback(
    debounce((val) => {
      dispatch(setWheelName(val)); // Dispatch action to update wheel name
      dispatch(pushToHistoryAction()); // Push the new state to history
    }, 500),
    []
  );

  const handleSubtitleChange = useCallback(
    debounce((val) => {
      dispatch(setSubTitle(val)); // Dispatch action to update subtitle
      dispatch(pushToHistoryAction()); // Push the new state to history
    }, 500),
    []
  );

  const handleDescriptionChange = useCallback(
    debounce((val) => {
      dispatch(setDescription(val)); // Dispatch action to update description
      dispatch(pushToHistoryAction()); // Push the new state to history
    }, 500),
    []
  );

  return (
    <div
      style={{ display: "flex", flexDirection: "column", marginBottom: "20px" }}
    >
      <FroalaTextEditor
        editorHeight={"80px"}
        name={"text_body_1"}
        id={1}
        value={wheel_name === "Untitled Wheel" ? "" : wheel_name}
        onChange={handleTitleChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef}
        label={"Wheel Name"}
        toolbarButtons={["|", "emoticons", "specialCharacters", "aiAssistant"]}
        type="title"
        characters="75"
        focused={froalaFocus === FROALA_FOCUS_ENUM.TITLE}
        placeholder="Untitled Wheel"
        dataEditorId="1"
      />

      <FroalaTextEditor
        placeholder="Subtitle"
        editorHeight={"80px"}
        name={"text_body_2"}
        id={2}
        value={sub_title}
        onChange={handleSubtitleChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef2}
        label={"Subtitle"}
        toolbarButtons={["|", "emoticons", "specialCharacters", "aiAssistant"]}
        type="title"
        characters="40"
        focused={froalaFocus === FROALA_FOCUS_ENUM.SUBTITLE}
        dataEditorId="2"
      />

      <FroalaTextEditor
        placeholder="Describe your Wheel"
        editorHeight={"150px"}
        name={"text_body_3"}
        id={3}
        value={description}
        onChange={handleDescriptionChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef3}
        label={"description"}
        dataEditorId="3"
      />

      {showAiPrompt &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 6522299,
              right: aiPromptPosition.right,
              top: aiPromptPosition.top,
              overflow: "auto",
            }}
          >
            <NewAIPrompt
              modalWidth="350px"
              completionType={currentCompletionType}
              onCloseIconPress={() => setShowAiPrompt(false)}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
